/**
 * Application Constants
 */

// Brand Colors
export const BRAND_COLORS = {
  primary: '#0052CC',
  primaryDark: '#0041A3',
  secondary: '#10b981',
  background: '#F4F5F7',
  white: '#FFFFFF',
  gray: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
} as const;

// ERP Systems
export const ERP_SYSTEMS = [
  'SAP',
  'Oracle',
  'Tally',
  'Zoho',
  'Microsoft Dynamics',
  'Custom',
] as const;

// Invoice Status
export const INVOICE_STATUS = {
  PROCESSED: 'processed',
  PENDING: 'pending',
  FAILED: 'failed',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  FTA_SUBMITTED: 'fta_submitted',
  FTA_VALIDATED: 'fta_validated',
  FTA_REJECTED: 'fta_rejected',
  AWAITING_FTA: 'awaiting_fta',
} as const;

// FTA E-Invoicing Status
export const FTA_STATUS = {
  NOT_SUBMITTED: 'not_submitted',
  SUBMITTED: 'submitted',
  VALIDATED: 'validated',
  ACKNOWLEDGED: 'acknowledged',
  REJECTED: 'rejected',
  FAILED: 'failed',
} as const;

// FTA Invoice Types
export const FTA_INVOICE_TYPES = {
  STANDARD: 'Standard Tax Invoice',
  SIMPLIFIED: 'Simplified Tax Invoice',
  CREDIT_NOTE: 'Credit Note',
  DEBIT_NOTE: 'Debit Note',
} as const;

// API Endpoints (for future use)
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/api/auth/login',
    LOGOUT: '/api/auth/logout',
    REFRESH: '/api/auth/refresh',
  },
  INVOICES: {
    LIST: '/api/invoices',
    CREATE: '/api/invoices',
    UPDATE: '/api/invoices/:id',
    DELETE: '/api/invoices/:id',
  },
  CLIENTS: {
    LIST: '/api/clients',
    CREATE: '/api/clients',
    UPDATE: '/api/clients/:id',
    DELETE: '/api/clients/:id',
  },
} as const;

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM dd, yyyy',
  FULL: 'MMMM dd, yyyy',
  SHORT: 'MM/dd/yyyy',
  ISO: "yyyy-MM-dd'T'HH:mm:ss",
} as const;

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  USER: 'ais_connect_user',
  TOKEN: 'ais_connect_token',
  THEME: 'ais_connect_theme',
  SELECTED_TRN: 'ais_connect_selected_trn',
  SELECTED_BRANCH: 'ais_connect_selected_branch',
} as const;

// Application Routes
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  DASHBOARD: '/dashboard',
  INVOICES: {
    SALES: '/invoices/sales',
    PURCHASE: '/invoices/purchase',
  },
  NOTES: {
    CREDIT: '/notes/credit',
    DEBIT: '/notes/debit',
  },
  REPORTS: '/reports',
  SETTINGS: '/settings',
  ADMIN: {
    CLIENTS: '/admin/clients',
    BILLING: '/admin/billing',
    LOGS: '/admin/logs',
  },
} as const;

// Validation Rules
export const VALIDATION = {
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE_REGEX: /^\+?[\d\s-()]+$/,
  MIN_PASSWORD_LENGTH: 8,
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
} as const;

// Environment
export const ENV = {
  API_URL: (import.meta as any).env?.VITE_API_URL || 'http://localhost:8000',
  APP_NAME: 'AIS Connect - UAE E-Invoicing Portal',
  APP_VERSION: '1.0.0',
} as const;

// UAE Emirates
export const UAE_EMIRATES = [
  'Abu Dhabi',
  'Dubai',
  'Sharjah',
  'Ajman',
  'Umm Al Quwain',
  'Ras Al Khaimah',
  'Fujairah',
] as const;

// UAE FTA Compliance
export const FTA_COMPLIANCE = {
  VAT_RATE: 5,
  ZERO_RATED: 0,
  TRN_LENGTH: 15,
  RETENTION_PERIOD_YEARS: 5,
  REAL_ESTATE_RETENTION_YEARS: 15,
  PEPPOL_FRAMEWORK: 'UAE Peppol',
  INVOICE_FORMATS: ['XML', 'JSON'],
  DIGITAL_SIGNATURE_REQUIRED: true,
  REAL_TIME_VALIDATION: true,
} as const;

// UAE Brand Colors (Green, Red, Black, Gold)
export const UAE_COLORS = {
  GREEN: '#00732F',
  RED: '#EE2A35',
  BLACK: '#000000',
  GOLD: '#FFD700',
  WHITE: '#FFFFFF',
} as const;

// FTA Gateway Configuration
export const FTA_GATEWAY = {
  ENDPOINT: 'https://fta.gov.ae/einvoicing/api/v1',
  TIMEOUT: 30000,
  RETRY_ATTEMPTS: 3,
  VALIDATION_ENDPOINT: '/validate',
  SUBMISSION_ENDPOINT: '/submit',
  ACKNOWLEDGMENT_ENDPOINT: '/acknowledge',
} as const;
